import { useScrollReveal, useScrollRevealGroup } from '@/hooks/useScrollReveal';

const plans = [
  {
    name: 'Старт',
    leads: '10 лидов',
    price: '990 ₽',
    perLead: '99 ₽ / лид',
    desc: 'Оптимально для тестирования канала и оценки качества лидов',
    features: [
      '10 квалифицированных лидов',
      'Контекст запроса',
      'Оценка намерения AI',
      'Источник лида',
      'Свои Telegram-чаты',
      'Поддержка в Telegram',
    ],
    cta: 'Получить 10 лидов',
    highlighted: false,
  },
  {
    name: 'Бизнес',
    leads: '100 лидов',
    price: '9 900 ₽',
    perLead: '99 ₽ / лид',
    desc: 'Для активного масштабирования отдела продаж',
    features: [
      '100 квалифицированных лидов',
      'Контекст запроса',
      'Оценка намерения AI',
      'Источник лида',
      'Свои Telegram-чаты',
      'Приоритетная поддержка',
      'Персональный менеджер',
    ],
    cta: 'Получить 100 лидов',
    highlighted: true,
  },
  {
    name: 'Максимум',
    leads: '300 лидов',
    price: '29 700 ₽',
    perLead: '99 ₽ / лид',
    desc: 'Максимальный охват — для команд с высокой пропускной способностью',
    features: [
      '300 квалифицированных лидов',
      'Контекст запроса',
      'Оценка намерения AI',
      'Источник лида',
      'Свои Telegram-чаты',
      'Приоритетная поддержка 24/7',
      'Персональный менеджер',
    ],
    cta: 'Получить 300 лидов',
    highlighted: false,
  },
];

export default function TLPricing() {
  const { ref: labelRef, revealed: labelRevealed } = useScrollReveal();
  const { ref: titleRef, revealed: titleRevealed } = useScrollReveal({ delay: 100 });
  const { ref: descRef, revealed: descRevealed } = useScrollReveal({ delay: 200 });
  const { containerRef: cardsRef, revealedCount: cardsRevealed } = useScrollRevealGroup<HTMLDivElement>(3);

  return (
    <section id="pricing" className="bg-background-50 py-20 md:py-28">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <span
            ref={labelRef}
            className={`inline-block text-[12px] font-semibold uppercase tracking-[0.18em] text-primary-600 reveal-fade-up ${labelRevealed ? 'revealed' : ''}`}
          >
            Тарифы
          </span>
          <h2
            ref={titleRef}
            className={`mt-4 font-display font-extrabold text-[34px] md:text-[46px] leading-[1.1] tracking-tightest text-foreground-950 reveal-fade-up ${titleRevealed ? 'revealed' : ''}`}
          >
            Подключение за 1 день, оплата помесячно
          </h2>
          <p
            ref={descRef}
            className={`mt-4 text-[16px] text-foreground-500 reveal-fade-up ${descRevealed ? 'revealed' : ''}`}
          >
            Без обязательств на год. Любой тариф можно сменить или отменить из личного кабинета.
          </p>
        </div>

        <div ref={cardsRef} className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {plans.map((plan, idx) => {
            const isHighlight = plan.highlighted;
            return (
              <div
                key={plan.name}
                className={`relative rounded-2xl p-7 md:p-8 border flex flex-col ${
                  isHighlight
                    ? 'bg-primary-500 text-background-50 border-primary-500 lg:scale-[1.02] lg:-translate-y-1'
                    : 'bg-background-50 text-foreground-900 border-background-200'
                } reveal-fade-up ${idx < cardsRevealed ? 'revealed' : ''}`}
                style={idx < cardsRevealed ? { transitionDelay: `${idx * 150}ms` } : undefined}
              >
                {isHighlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center px-3 py-1.5 rounded-full bg-background-50 text-primary-500 text-[11px] font-bold uppercase tracking-wider whitespace-nowrap">
                    Популярный
                  </span>
                )}

                <div className="font-display text-[15px] font-bold text-foreground-950">
                  {plan.name}
                </div>
                <div className={`mt-1 text-[13px] ${isHighlight ? 'text-background-50/80' : 'text-foreground-400'}`}>
                  {plan.leads}
                </div>

                <div className="mt-6 flex items-baseline gap-1">
                  <span className={`font-display text-[34px] md:text-[40px] font-extrabold tracking-tightest leading-none ${isHighlight ? 'text-background-50' : 'text-foreground-950'}`}>
                    {plan.price}
                  </span>
                </div>
                <div className={`mt-1 text-[13px] ${isHighlight ? 'text-background-50/70' : 'text-foreground-400'}`}>
                  {plan.perLead}
                </div>

                <p className={`mt-5 text-[14px] leading-relaxed ${isHighlight ? 'text-background-50/90' : 'text-foreground-500'}`}>
                  {plan.desc}
                </p>

                <ul className="mt-6 space-y-3 flex-1">
                  {plan.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-2.5 text-[14px]">
                      <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${isHighlight ? 'bg-background-50 text-primary-500' : 'bg-primary-50 text-primary-500'}`}>
                        <i className="ri-check-line text-[12px]"></i>
                      </span>
                      <span className={isHighlight ? 'text-background-50/90' : 'text-foreground-700'}>{feat}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="https://lk.aigam.ru"
                  className={`mt-8 h-12 inline-flex items-center justify-center rounded-md font-semibold text-[14.5px] transition-colors cursor-pointer whitespace-nowrap ${
                    isHighlight
                      ? 'bg-background-50 text-primary-500 hover:bg-background-100'
                      : 'bg-primary-500 hover:bg-primary-600 text-background-50'
                  }`}
                >
                  {plan.cta}
                </a>
              </div>
            );
          })}
        </div>

        <p className="mt-8 text-center text-[13px] text-foreground-400">
          Возможна оплата от ИП и юр. лица. Закрывающие документы предоставляются.
        </p>
      </div>
    </section>
  );
}